import React, { useState, useEffect } from 'react';
import DragDropUpload from './DragDropUpload';
import FormSelect from './FormSelect';
import FormModal from './FormModal';
import ArtistForm from '../../Artists/components/ArtistForm';
import DistributionForm from '../../Distributor/components/DistributionForm';
import RingtoneForm from '../../Ringtone/components/RingtoneForm';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import en from '../../../translations/en.json';
import { CREATE_ARTIST_URL, GET_ARTISTS_URL } from '../../../api/artist';
import { GET_SONGS_URL, CHECK_SONG_NAME_URL } from '../../../api/song';
import { GET_DISTRIBUTORS_URL, CREATE_DISTRIBUTOR_URL } from '../../../api/distributor';
import { GET_RINGTONES_URL, CREATE_RINGTONE_URL } from '../../../api/ringtone';
import { apiClient } from '../../../api/apiClient';
import { toast } from '../../../components/shared/toast';
import { createDistributorOption } from '../../Distributor/utils/distributorOptions';
import './AddSong.css';

function AddSong({ song, onCancel, onSave }) {
  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };
  // Form Fields State
  const [track, setTrack] = useState(
    song?.trackUrl ? { name: song.trackUrl.split('/').pop(), size: 0, previewUrl: song.trackUrl } : null
  );
  const [art, setArt] = useState(
    song?.imageUrl ? { name: song.imageUrl.split('/').pop(), size: 0, previewUrl: song.imageUrl } : null
  );
  const [songNameSinhala, setSongNameSinhala] = useState(song?.nameSinhala || '');
  const [songName, setSongName] = useState(song?.name || '');
  const [selectedArtists, setSelectedArtists] = useState(song?.singers || []);
  const [selectedLyrics, setSelectedLyrics] = useState(song?.lyricists || []);
  const [selectedMusic, setSelectedMusic] = useState(song?.musicians || []);
  // Main artist IDs — determines who receives revenue for each role
  const [mainSinger, setMainSinger] = useState(
    song?.mainSingerId ? String(song.mainSingerId) : (song?.singers?.length > 0 ? String(song.singers[0]) : null)
  );
  const [mainLyricist, setMainLyricist] = useState(
    song?.mainLyricistId ? String(song.mainLyricistId) : (song?.lyricists?.length > 0 ? String(song.lyricists[0]) : null)
  );
  const [mainMusician, setMainMusician] = useState(
    song?.mainMusicianId ? String(song.mainMusicianId) : (song?.musicians?.length > 0 ? String(song.musicians[0]) : null)
  );
  const [isrcCode, setIsrcCode] = useState(song?.isrcCode || '');
  const [other, setOther] = useState(song?.other || '');
  const [originalOrVersion, setOriginalOrVersion] = useState(song?.versionType || '');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSongData, setPendingSongData] = useState(null);
  const [sinhalaSuggestions, setSinhalaSuggestions] = useState([]);
  const [englishSuggestions, setEnglishSuggestions] = useState([]);
  const [isSinhalaSuggestionsLoading, setIsSinhalaSuggestionsLoading] = useState(false);
  const [isEnglishSuggestionsLoading, setIsEnglishSuggestionsLoading] = useState(false);
  const [activeSuggestionField, setActiveSuggestionField] = useState(null);

  // Versions Panel Accordion
  const [isVersionsExpanded, setIsVersionsExpanded] = useState(true);
  const [versionName, setVersionName] = useState(song?.versionName || '');
  const [selectedOriginalSong, setSelectedOriginalSong] = useState(song?.originalSongId ? String(song.originalSongId) : '');

  // Distribution & Ringtone Section Toggles
  const [distributionEnabled, setDistributionEnabled] = useState(!!song?.distributionProvider);
  const [selectedDistribution, setSelectedDistribution] = useState(song?.distributionProvider || '');

  const [ringtoneEnabled, setRingtoneEnabled] = useState(!!(song?.ringtones && song.ringtones.length > 0));
  const [ringtoneCards, setRingtoneCards] = useState(
    song?.ringtones && song.ringtones.length > 0
      ? song.ringtones.map(r => ({ provider: r.ringintoneId || '', ringtoneCode: r.ringtoneCode || '', contentCode: r.contentCode || '' }))
      : [{ provider: '', ringtoneCode: '', contentCode: '' }]
  );



  const [distributionOptions, setDistributionOptions] = useState([]);
  const [ringtoneOptions, setRingtoneOptions] = useState([]);

  useEffect(() => {
    if (distributionEnabled && distributionOptions.length === 0) {
      const fetchDistributors = async () => {
        try {
          const distData = await apiClient(`${GET_DISTRIBUTORS_URL}?limit=1000&status=1`);
          if (distData && distData.distributors) {
            setDistributionOptions(distData.distributors.map((distributor) => createDistributorOption(distributor)).filter(Boolean));
          }
        } catch (err) {
          console.warn('Failed to load distributors list:', err);
        }
      };
      fetchDistributors();
    }
  }, [distributionEnabled, distributionOptions.length]);

  useEffect(() => {
    if (ringtoneEnabled && ringtoneOptions.length === 0) {
      const fetchRingtones = async () => {
        try {
          const ringData = await apiClient(`${GET_RINGTONES_URL}?limit=1000&status=1`);
          if (ringData && ringData.ringtones) {
            setRingtoneOptions(ringData.ringtones.map(r => ({
              value: String(r.id),
              label: r.name
            })));
          }
        } catch (err) {
          console.warn('Failed to load ringtones list:', err);
        }
      };
      fetchRingtones();
    }
  }, [ringtoneEnabled, ringtoneOptions.length]);

  useEffect(() => {
    const term = songNameSinhala.trim();
    if (term.length < 2) {
      setSinhalaSuggestions([]);
      setIsSinhalaSuggestionsLoading(false);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsSinhalaSuggestionsLoading(true);
        const response = await apiClient(`${GET_SONGS_URL}?page=1&limit=6&search=${encodeURIComponent(term)}`);
        const items = (response?.songs || []).filter((song) => song && (song.name || song.nameSinhala));
        setSinhalaSuggestions(items);
      } catch (err) {
        console.warn('Failed to load Sinhala song suggestions:', err);
        setSinhalaSuggestions([]);
      } finally {
        setIsSinhalaSuggestionsLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [songNameSinhala]);

  useEffect(() => {
    const term = songName.trim();
    if (term.length < 2) {
      setEnglishSuggestions([]);
      setIsEnglishSuggestionsLoading(false);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsEnglishSuggestionsLoading(true);
        const response = await apiClient(`${GET_SONGS_URL}?page=1&limit=6&search=${encodeURIComponent(term)}`);
        const items = (response?.songs || []).filter((song) => song && (song.name || song.nameSinhala));
        setEnglishSuggestions(items);
      } catch (err) {
        console.warn('Failed to load English song suggestions:', err);
        setEnglishSuggestions([]);
      } finally {
        setIsEnglishSuggestionsLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [songName]);

  const handleSuggestionSelect = (song) => {
    setSongNameSinhala(song?.nameSinhala || song?.name || '');
    setSongName(song?.name || '');
    setSinhalaSuggestions([]);
    setEnglishSuggestions([]);
    setActiveSuggestionField(null);
  };

  // Modal State for adding items inline
  const [modalType, setModalType] = useState(null); // 'artist' | 'lyrics' | 'music' | 'distribution' | 'ringtone'
  const [newValName, setNewValName] = useState('');
  const [validationError, setValidationError] = useState('');

  // Other word count limit
  const maxWords = 350;
  const wordCount = other.trim() === '' ? 0 : other.trim().split(/\s+/).length;

  const handleOtherChange = (e) => {
    const text = e.target.value;
    const words = text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
    if (words <= maxWords) {
      setOther(text);
    }
  };

  // Inline Creation form handler
  const handleAddNewItem = (e) => {
    e.preventDefault();
    if (!newValName.trim()) return;

    const key = newValName.trim().toLowerCase().replace(/\s+/g, '_');
    const labelText = newValName.trim();

    if (modalType === 'artist') {
      setSelectedArtists([...selectedArtists, key]);
    } else if (modalType === 'lyrics') {
      setSelectedLyrics([...selectedLyrics, key]);
    } else if (modalType === 'music') {
      setSelectedMusic([...selectedMusic, key]);
    } else if (modalType === 'distribution') {
      setDistributionOptions([...distributionOptions, { value: key, label: labelText }]);
      setSelectedDistribution(key);
    } else if (modalType === 'ringtone') {
      setRingtoneOptions([...ringtoneOptions, { value: key, label: labelText }]);
    }

    setNewValName('');
    setModalType(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Form validations using warnings retrieved from translation file
    if (!track) {
      toast.warning(t('songs.validationTrackRequired'));
      return;
    }
    if (!songNameSinhala.trim()) {
      toast.warning(t('songs.validationSinhalaNameRequired'));
      return;
    }
    if (!songName.trim()) {
      toast.warning(t('songs.validationNameRequired'));
      return;
    }
    if (selectedArtists.length === 0) {
      toast.warning(t('songs.validationArtistRequired'));
      return;
    }
    if (!isrcCode.trim()) {
      toast.warning('ISRC Code is required');
      return;
    }
    if (!originalOrVersion) {
      toast.warning(t('songs.validationTypeRequired'));
      return;
    }

    if (originalOrVersion === 'Version') {
      if (!versionName.trim()) {
        toast.warning(t('songs.validationVersionNameRequired'));
        return;
      }
      if (!selectedOriginalSong) {
        toast.warning(t('songs.validationOriginalSongRequired'));
        return;
      }
    }

    const payload = {
      track,
      art,
      songNameSinhala: songNameSinhala.trim(),
      songName: songName.trim(),
      artists: selectedArtists,
      lyrics: selectedLyrics,
      music: selectedMusic,
      // Main artist IDs for revenue distribution per role
      mainSinger: mainSinger || (selectedArtists.length > 0 ? String(selectedArtists[0]) : null),
      mainLyricist: mainLyricist || (selectedLyrics.length > 0 ? String(selectedLyrics[0]) : null),
      mainMusician: mainMusician || (selectedMusic.length > 0 ? String(selectedMusic[0]) : null),
      isrcCode,
      other,
      originalOrVersion,
      versionDetails: originalOrVersion === 'Version' ? { versionName, originalSong: selectedOriginalSong } : null,
      distribution: distributionEnabled ? { provider: selectedDistribution } : null,
      ringtones: ringtoneEnabled ? ringtoneCards : null
    };

    // Ringtone validation when toggle is active
    if (ringtoneEnabled) {
      if (ringtoneCards.length === 0) {
        toast.warning('At least one ringtone entry is required when ringtone is enabled');
        return;
      }
      for (let i = 0; i < ringtoneCards.length; i++) {
        const card = ringtoneCards[i];
        if (!card.provider) {
          toast.warning(`Ringtone ${i + 1}: Please select a ringtone provider`);
          return;
        }
        if (!card.ringtoneCode.trim()) {
          toast.warning(`Ringtone ${i + 1}: Ringtone Code is required`);
          return;
        }
        if (!card.contentCode.trim()) {
          toast.warning(`Ringtone ${i + 1}: Content Code is required`);
          return;
        }
      }
      // Check for duplicate providers
      const providers = ringtoneCards.map(c => c.provider);
      const uniqueProviders = new Set(providers);
      if (uniqueProviders.size !== providers.length) {
        toast.warning('Duplicate ringtone providers are not allowed. Each provider can only be added once.');
        return;
      }
    }

    // Check same song name on backend before proceeding
    try {
      const response = await apiClient(CHECK_SONG_NAME_URL, {
        method: 'POST',
        body: { name: payload.songName }
      });
      if (response && response.exists) {
        setPendingSongData(payload);
        setShowConfirmModal(true);
      } else {
        proceedSaveSong(payload);
      }
    } catch (err) {
      console.warn('Song duplicate name check failed:', err);
      proceedSaveSong(payload);
    }
  };

  const proceedSaveSong = (payload) => {
    onSave?.(payload);
    onCancel?.();
  };

  const handleConfirmDuplicate = () => {
    if (pendingSongData) {
      proceedSaveSong(pendingSongData);
    }
    setShowConfirmModal(false);
    setPendingSongData(null);
  };

  const handleCancelDuplicate = () => {
    setShowConfirmModal(false);
    setPendingSongData(null);
  };

  const originalOrVersionsOptions = [
    { value: 'Original', label: 'Original' },
    { value: 'Version', label: 'Version' }
  ];

  return (
    <div className="add-song-wrapper">
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb-nav">
        <button type="button" className="breadcrumb-link" onClick={onCancel}>{t('songs.breadcrumb')}</button>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">{song ? 'Edit Song' : t('common.addNew')}</span>
      </nav>

      {/* Header section */}
      <div className="add-song-header-row">
        <div className="header-icon-box">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M9 18V5l12-2v13" />
            <circle cx="6" cy="18" r="3" />
            <circle cx="18" cy="16" r="3" />
          </svg>
        </div>
        <div className="header-text-container">
          <h1 className="add-song-title">{song ? 'Edit Song Details' : t('songs.addNewTitle')}</h1>
          <p className="add-song-subtitle">{song ? 'Modify song metadata details.' : t('songs.addNewSubtitle')}</p>
        </div>
      </div>

      <form onSubmit={handleSave} className="add-song-form-container" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* File Uploads Card */}
        <div className="form-card">
          {/* File Uploads Grid */}
          <div className="form-grid-2col">
            <DragDropUpload
              label={t('songs.trackLabel')}
              required={true}
              accept=".mp3"
              infoText={t('songs.trackUploadInfo')}
              onValidationError={setValidationError}
              value={track}
              onChange={setTrack}
            />

            <DragDropUpload
              label={t('songs.artLabel')}
              required={false}
              accept="image/*"
              infoText={t('songs.artUploadInfo')}
              subInfoText={t('songs.artUploadSubInfo')}
              onValidationError={setValidationError}
              value={art}
              onChange={setArt}
            />
          </div>

          {validationError && (
            <div className="validation-error-banner" style={{ color: '#ef4444', fontSize: '13px', fontWeight: '500' }}>
              {validationError}
            </div>
          )}
        </div>

        {/* Names Card */}
        <div className="form-card">
          {/* Names Row */}
          <div className="form-grid-2col">
            <div className="form-field-group">
              <label className="form-field-label">
                {t('songs.nameSinhalaLabel')} <span className="required-asterisk">*</span>
              </label>
              <div className="song-name-input-shell">
                <input
                  type="text"
                  className="form-text-input"
                  placeholder={t('songs.nameSinhalaPlaceholder')}
                  value={songNameSinhala}
                  onChange={(e) => setSongNameSinhala(e.target.value)}
                  onFocus={() => setActiveSuggestionField('sinhala')}
                />
                {(activeSuggestionField === 'sinhala' && (isSinhalaSuggestionsLoading || sinhalaSuggestions.length > 0)) && (
                  <div className="song-name-suggestions">
                    {isSinhalaSuggestionsLoading ? (
                      <div className="song-name-suggestion-empty">Searching songs...</div>
                    ) : (
                      sinhalaSuggestions.map((song) => (
                        <button
                          key={song.id}
                          type="button"
                          className="song-name-suggestion-item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSuggestionSelect(song);
                          }}
                        >
                          <span className="song-name-suggestion-main">{song.nameSinhala || song.name}</span>
                          {song.name && song.nameSinhala && (
                            <span className="song-name-suggestion-sub">{song.name}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-field-group">
              <label className="form-field-label">
                {t('songs.nameLabel')} <span className="required-asterisk">*</span>
              </label>
              <div className="song-name-input-shell">
                <input
                  type="text"
                  className="form-text-input"
                  placeholder={t('songs.namePlaceholder')}
                  value={songName}
                  onChange={(e) => setSongName(e.target.value)}
                  onFocus={() => setActiveSuggestionField('english')}
                />
                {(activeSuggestionField === 'english' && (isEnglishSuggestionsLoading || englishSuggestions.length > 0)) && (
                  <div className="song-name-suggestions">
                    {isEnglishSuggestionsLoading ? (
                      <div className="song-name-suggestion-empty">Searching songs...</div>
                    ) : (
                      englishSuggestions.map((song) => (
                        <button
                          key={song.id}
                          className="song-name-suggestion-item"
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSuggestionSelect(song);
                          }}
                        >
                          <span className="song-name-suggestion-main">{song.name}</span>
                          {song.nameSinhala && (
                            <span className="song-name-suggestion-sub">{song.nameSinhala}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Artist & Lyrics Card */}
        <div className="form-card">
          {/* Artist & Lyrics Row */}
          <div className="form-grid-2col">
            <FormSelect
              label={t('songs.artistLabel')}
              required={true}
              value={selectedArtists}
              onChange={(newVal) => {
                setSelectedArtists(newVal);
                // If current main was removed, reset to first remaining
                if (mainSinger && !newVal.map(String).includes(String(mainSinger))) {
                  setMainSinger(newVal.length > 0 ? String(newVal[0]) : null);
                }
                // Auto-assign main if none set
                if (!mainSinger && newVal.length > 0) {
                  setMainSinger(String(newVal[0]));
                }
              }}
              isMulti={true}
              placeholder={t('songs.artistPlaceholder')}
              onAddNewLabel={() => setModalType('artist')}
              isLazy={true}
              mainValue={mainSinger}
              onMainChange={setMainSinger}
            />

            <FormSelect
              label={t('songs.lyricsLabel')}
              required={false}
              value={selectedLyrics}
              onChange={(newVal) => {
                setSelectedLyrics(newVal);
                if (mainLyricist && !newVal.map(String).includes(String(mainLyricist))) {
                  setMainLyricist(newVal.length > 0 ? String(newVal[0]) : null);
                }
                if (!mainLyricist && newVal.length > 0) {
                  setMainLyricist(String(newVal[0]));
                }
              }}
              isMulti={true}
              placeholder={t('songs.lyricsPlaceholder')}
              onAddNewLabel={() => setModalType('lyrics')}
              isLazy={true}
              mainValue={mainLyricist}
              onMainChange={setMainLyricist}
            />
          </div>
        </div>

        {/* Music & ISRC Code Card */}
        <div className="form-card">
          {/* Music & ISRC Code Row */}
          <div className="form-grid-2col">
            <FormSelect
              label={t('songs.musicLabel')}
              required={false}
              value={selectedMusic}
              onChange={(newVal) => {
                setSelectedMusic(newVal);
                if (mainMusician && !newVal.map(String).includes(String(mainMusician))) {
                  setMainMusician(newVal.length > 0 ? String(newVal[0]) : null);
                }
                if (!mainMusician && newVal.length > 0) {
                  setMainMusician(String(newVal[0]));
                }
              }}
              isMulti={true}
              placeholder={t('songs.musicPlaceholder')}
              onAddNewLabel={() => setModalType('music')}
              isLazy={true}
              mainValue={mainMusician}
              onMainChange={setMainMusician}
            />

            <div className="form-field-group">
              <label className="form-field-label">
                {t('songs.isrcLabel')} <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                className="form-text-input"
                placeholder={t('songs.isrcPlaceholder')}
                value={isrcCode}
                onChange={(e) => setIsrcCode(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Description Textarea Card */}
        <div className="form-card">
          {/* Description Textarea */}
          <div className="form-field-group">
            <label className="form-field-label">{t('songs.otherLabel')}</label>
            <textarea
              className="form-textarea"
              placeholder={t('songs.otherPlaceholder')}
              value={other}
              onChange={handleOtherChange}
            />
            <div className="textarea-counter-row">
              {wordCount} / {maxWords}
            </div>
          </div>
        </div>

        {/* Original or Versions Selector Card */}
        <div className="form-card">
          {/* Original or Versions Selector */}
          <div className="form-field-group">
            <FormSelect
              label={t('songs.typeLabel')}
              required={true}
              options={originalOrVersionsOptions}
              value={originalOrVersion}
              onChange={(val) => {
                setOriginalOrVersion(val);
                if (val === 'Version') {
                  setIsVersionsExpanded(true);
                }
              }}
              placeholder={t('songs.typePlaceholder')}
            />
          </div>
        </div>

        {/* Versions Expandable accordion */}
        {originalOrVersion === 'Version' && (
          <div className="accordion-section">
            <div
              className="accordion-header"
              onClick={() => setIsVersionsExpanded(!isVersionsExpanded)}
            >
              <span className="accordion-title">{t('songs.versionsLabel')}</span>
              <div className="accordion-chevron-box">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`chevron-icon ${isVersionsExpanded ? 'rotate' : ''}`}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>

            {isVersionsExpanded && (
              <div className="accordion-body">
                <div className="form-grid-2col">
                  <div className="form-field-group">
                    <label className="form-field-label">
                      {t('songs.versionNameLabel')} <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-text-input"
                      placeholder={t('songs.versionNamePlaceholder')}
                      value={versionName}
                      onChange={(e) => setVersionName(e.target.value)}
                    />
                  </div>

                  <FormSelect
                    label={t('songs.originalSongNameLabel')}
                    required={true}
                    value={selectedOriginalSong}
                    onChange={setSelectedOriginalSong}
                    placeholder={t('songs.originalSongNamePlaceholder')}
                    isLazy={true}
                    lazyType="songs"
                    lazyParams={{ versionType: 'Original', ...(song?.id ? { excludeId: song.id } : {}) }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Distribution & Ringtone Section */}
        <div className="form-card">
          {/* Distribution */}
          <div className="toggle-section-wrapper">
            <div className="toggle-row-header">
              <span className="toggle-section-title">{t('songs.distributionLabel')}</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={distributionEnabled}
                  onChange={(e) => setDistributionEnabled(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
            </div>

            {distributionEnabled && (
              <div className="toggle-body-content" style={{ marginTop: '16px' }}>
                <FormSelect
                  label={t('songs.distributionLabel')}
                  options={distributionOptions}
                  value={selectedDistribution}
                  onChange={setSelectedDistribution}
                  placeholder={t('songs.distributionPlaceholder')}
                  onAddNewInput={() => setModalType('distribution')}
                />
              </div>
            )}
          </div>

          <hr className="toggle-divider" />

          {/* Ringtone */}
          <div className="toggle-section-wrapper">
            <div className="toggle-row-header">
              <span className="toggle-section-title">{t('songs.ringtoneLabel')}</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={ringtoneEnabled}
                  onChange={(e) => setRingtoneEnabled(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
            </div>

            {ringtoneEnabled && (
              <div className="toggle-body-content" style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {ringtoneCards.map((card, index) => (
                  <div key={index} style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', position: 'relative', background: '#f8fafc' }}>
                    {ringtoneCards.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          setRingtoneCards(prev => prev.filter((_, i) => i !== index));
                        }}
                        style={{
                          position: 'absolute', top: '8px', right: '8px',
                          background: '#ef4444', color: 'white', border: 'none',
                          borderRadius: '50%', width: '24px', height: '24px',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '14px', fontWeight: 'bold', lineHeight: 1
                        }}
                        title="Remove this ringtone"
                      >
                        ×
                      </button>
                    )}
                    <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#64748b', marginBottom: '12px' }}>
                      Ringtone {index + 1}
                    </div>
                    <FormSelect
                      label={<>{t('songs.ringtoneLabel')} <span className="required-asterisk">*</span></>}
                      options={ringtoneOptions}
                      value={card.provider}
                      onChange={(val) => {
                        setRingtoneCards(prev => prev.map((c, i) => i === index ? { ...c, provider: val } : c));
                      }}
                      placeholder={t('songs.ringtonePlaceholder')}
                      onAddNewInput={() => setModalType('ringtone')}
                    />

                    <div className="form-grid-3col" style={{ marginTop: '12px' }}>
                      <div className="form-field-group">
                        <label className="form-field-label">{t('songs.ringtoneIdLabel')} <span className="required-asterisk">*</span></label>
                        <input
                          type="text"
                          className="form-text-input"
                          placeholder={t('songs.ringtoneIdPlaceholder')}
                          value={card.ringtoneCode}
                          onChange={(e) => {
                            setRingtoneCards(prev => prev.map((c, i) => i === index ? { ...c, ringtoneCode: e.target.value } : c));
                          }}
                        />
                      </div>

                      <div className="form-field-group">
                        <label className="form-field-label">{t('songs.contentCodeLabel')} <span className="required-asterisk">*</span></label>
                        <input
                          type="text"
                          className="form-text-input"
                          placeholder={t('songs.contentCodePlaceholder')}
                          value={card.contentCode}
                          onChange={(e) => {
                            setRingtoneCards(prev => prev.map((c, i) => i === index ? { ...c, contentCode: e.target.value } : c));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    setRingtoneCards(prev => [...prev, { provider: '', ringtoneCode: '', contentCode: '' }]);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    background: 'none', border: '1px dashed #94a3b8', borderRadius: '8px',
                    padding: '10px 16px', cursor: 'pointer', color: '#3b82f6',
                    fontSize: '0.9rem', fontWeight: '500', width: '100%', justifyContent: 'center'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add Another Ringtone
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="form-actions-footer">
          <button
            type="button"
            className="form-btn-outlined"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>

          <button
            type="submit"
            className="form-btn-primary"
          >
            {/* Save Icon */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            <span>{t('common.saveSong')}</span>
          </button>
        </div>
      </form>

      {/* Add New Modal Dialogs */}
      <FormModal
        isOpen={modalType !== null}
        onClose={() => {
          setModalType(null);
          setNewValName('');
        }}
        title={
          modalType === 'artist'
            ? t('artists.modalTitle')
            : modalType === 'lyrics'
              ? 'Add New Lyricist'
              : modalType === 'music'
                ? 'Add New Musician'
                : modalType === 'distribution'
                  ? t('distributor.addNewTitle')
                  : modalType === 'ringtone'
                    ? t('ringtone.addNewTitle')
                    : `Add New ${modalType ? modalType.charAt(0).toUpperCase() + modalType.slice(1) : ''}`
        }
        className={['artist', 'lyrics', 'music', 'distribution', 'ringtone'].includes(modalType) ? 'modal-wide' : ''}
      >
        {['artist', 'lyrics', 'music'].includes(modalType) ? (
          <ArtistForm
            isModal={true}
            onCancel={() => {
              setModalType(null);
            }}
            onSave={async (newArtistData) => {
              try {
                // Assemble FormData locally and call backend API endpoint
                 const formData = new FormData();
                 formData.append('name', newArtistData.artistName);
                 formData.append('gender', newArtistData.gender);
                 formData.append('status', String(newArtistData.status));
                 
                 if (Array.isArray(newArtistData.artistTypes)) {
                  newArtistData.artistTypes.forEach((type) => {
                    formData.append('types', type);
                  });
                }

                if (newArtistData.artistImage && newArtistData.artistImage.file) {
                  formData.append('image', newArtistData.artistImage.file);
                }

                const savedArtist = await apiClient(CREATE_ARTIST_URL, {
                  method: 'POST',
                  body: formData,
                });
                
                const labelText = savedArtist.name || newArtistData.artistName;
                const valueKey = savedArtist.id ? String(savedArtist.id) : newArtistData.artistName.toLowerCase().replace(/\s+/g, '_');

                if (modalType === 'artist') {
                  setSelectedArtists(prev => [...prev, valueKey]);
                } else if (modalType === 'lyrics') {
                  setSelectedLyrics(prev => [...prev, valueKey]);
                } else if (modalType === 'music') {
                  setSelectedMusic(prev => [...prev, valueKey]);
                }

                toast.success('Artist created successfully in Backend!');
                setModalType(null);
              } catch (err) {
                console.warn('Backend artist creation failed, falling back to mock:', err);
                
                const valueKey = newArtistData.artistName.toLowerCase().replace(/\s+/g, '_');

                if (modalType === 'artist') {
                  setSelectedArtists(prev => [...prev, valueKey]);
                } else if (modalType === 'lyrics') {
                  setSelectedLyrics(prev => [...prev, valueKey]);
                } else if (modalType === 'music') {
                  setSelectedMusic(prev => [...prev, valueKey]);
                }

                toast.success('Artist saved successfully (Mock)!');
                setModalType(null);
              }
            }}
          />
        ) : modalType === 'distribution' ? (
          <DistributionForm
            isModal={true}
            onCancel={() => {
              setModalType(null);
            }}
            onSave={async (newDistData) => {
              try {
                const response = await apiClient(CREATE_DISTRIBUTOR_URL, {
                  method: 'POST',
                  body: newDistData
                });
                if (response) {
                  toast.success('Distributor created successfully!');
                  const newOption = createDistributorOption(response);
                  if (newOption) {
                    setDistributionOptions(prev => [...prev, newOption]);
                  }
                  setSelectedDistribution(String(response.id));
                  setModalType(null);
                }
              } catch (e) {
                console.error(e);
                toast.error('Failed to save distributor: ' + (e.message || ''));
              }
            }}
          />
        ) : modalType === 'ringtone' ? (
          <RingtoneForm
            isModal={true}
            onCancel={() => {
              setModalType(null);
            }}
            onSave={async (newRingtoneData) => {
              try {
                const formData = new FormData();
                formData.append('name', newRingtoneData.name);
                if (newRingtoneData.logo && newRingtoneData.logo.file) {
                  formData.append('logo', newRingtoneData.logo.file);
                }
                const response = await apiClient(CREATE_RINGTONE_URL, {
                  method: 'POST',
                  body: formData
                });
                if (response) {
                  toast.success('Ringtone created successfully!');
                  setRingtoneOptions(prev => [...prev, { value: String(response.id), label: response.name }]);
                  setSelectedRingtone(String(response.id));
                  setModalType(null);
                }
              } catch (e) {
                console.error(e);
                toast.error('Failed to save ringtone operator: ' + (e.message || ''));
                throw e;
              }
            }}
            existingNames={ringtoneOptions.map(r => r.label)}
          />
        ) : (
          <form onSubmit={handleAddNewItem} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="form-field-group">
              <label className="form-field-label">
                Name <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                className="form-text-input"
                placeholder={`Enter new ${modalType || ''} name`}
                value={newValName}
                onChange={(e) => setNewValName(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="modal-form-actions">
              <button
                type="button"
                className="form-btn-outlined"
                onClick={() => {
                  setModalType(null);
                  setNewValName('');
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="form-btn-primary"
              >
                Add Item
              </button>
            </div>
          </form>
        )}
      </FormModal>

      <ConfirmationModal
        isOpen={showConfirmModal}
        title={t('songs.confirmDuplicateTitle')}
        message={t('songs.confirmDuplicateMessage')}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirmDuplicate}
        onCancel={handleCancelDuplicate}
      />
    </div>
  );
}

export default AddSong;
